import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refresh } = useAuth();
  const [state, setState] = useState({ phase: "checking", plan: null });

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error" });
      return;
    }
    let attempts = 0;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          await refresh();
          setState({ phase: "paid", plan: r.data.plan });
          return;
        }
        if (r.data.status === "expired" || r.data.payment_status === "failed") {
          setState({ phase: "failed" });
          return;
        }
      } catch (_) {}
      if (attempts >= 8) {
        setState({ phase: "timeout" });
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, refresh]);

  return (
    <Layout>
      <div className="max-w-xl mx-auto bg-[var(--pa-surface)] border border-[var(--pa-border-active)] rounded-lg p-10 text-center mt-10">
        {state.phase === "checking" && (
          <>
            <Loader2 size={36} className="text-[var(--pa-gold)] animate-spin mx-auto" />
            <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-5">Confirming your payment…</h2>
            <p className="mt-2 text-sm text-[var(--pa-text-muted)]">
              This usually takes a few seconds.
            </p>
          </>
        )}
        {state.phase === "paid" && (
          <>
            <CheckCircle2 size={42} className="text-emerald-400 mx-auto" />
            <h2 className="font-serif text-3xl text-[var(--pa-text)] mt-5" data-testid="payment-success">
              Subscription active
            </h2>
            <p className="mt-3 text-sm text-[var(--pa-text-muted)]">
              You're now on the <span className="text-[var(--pa-gold)] font-semibold">{state.plan?.toUpperCase()}</span> plan.
              All 11 clinical modules are unlocked.
            </p>
            <Link
              to="/dashboard"
              data-testid="goto-dashboard"
              className="inline-block mt-7 bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold px-6 py-3 rounded-md hover:bg-[var(--pa-gold-hover)] transition"
            >
              Go to dashboard
            </Link>
          </>
        )}
        {(state.phase === "failed" || state.phase === "error" || state.phase === "timeout") && (
          <>
            <XCircle size={42} className="text-red-400 mx-auto" />
            <h2 className="font-serif text-2xl text-[var(--pa-text)] mt-5">Payment not completed</h2>
            <p className="mt-3 text-sm text-[var(--pa-text-muted)]">
              {state.phase === "timeout"
                ? "We couldn't confirm the payment in time. Please refresh your account page."
                : "Something went wrong. Please try again from the Account page."}
            </p>
            <Link
              to="/account"
              className="inline-block mt-7 border border-[var(--pa-gold)] text-[var(--pa-gold)] font-semibold px-6 py-3 rounded-md hover:bg-[var(--pa-gold)]/10 transition"
            >
              Back to Account
            </Link>
          </>
        )}
      </div>
    </Layout>
  );
}
